import { useRef } from 'react';
import type { ReactNode } from 'react';
import { AppDialog, DialogButton } from './AppDialog';

export interface ConfirmDialogProps {
    isOpen: boolean;
    /** Invoked for Escape, the close affordance and the cancel button. */
    onClose: () => void;
    /** Invoked by the confirm action button. */
    onConfirm?: () => void | Promise<void>;
    /** Bold alert title (e.g. "Delete workset?"). */
    title?: ReactNode;
    /** Explanatory body text. */
    message?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** `destructive` renders the confirm action in red and defaults focus to Cancel. */
    tone?: 'default' | 'destructive';
    isConfirmDisabled?: boolean;
    /** Hide the cancel button for a single-action alert. */
    hideCancel?: boolean;
}

/**
 * The shared confirmation alert (Apple HIG alert pattern): a compact,
 * centered panel with a title, an explanatory message and a right-aligned
 * action pair. Use this instead of `window.confirm` or hand-rolled
 * AlertDialogs so every prompt shares one look and behavior.
 */
export function ConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel = 'OK',
    cancelLabel = 'Cancel',
    tone = 'default',
    isConfirmDisabled = false,
    hideCancel = false,
}: ConfirmDialogProps) {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    // HIG: in destructive alerts the safe action (Cancel) takes default focus;
    // otherwise the confirm action does.
    const initialFocusRef = tone === 'destructive' ? cancelRef : confirmRef;


    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="xs"
            variant="alert"
            closeOnOverlayClick={false}
            showCloseButton={false}
            initialFocusRef={initialFocusRef}
            footer={
                <>
                    {!hideCancel && (
                        <DialogButton
                            ref={cancelRef}
                            variant="secondary"
                            onClick={onClose}
                        >
                            {cancelLabel}
                        </DialogButton>
                    )}
                    <DialogButton
                        ref={confirmRef}
                        variant={tone === 'destructive' ? 'destructive' : 'primary'}
                        onClick={onConfirm}
                        isDisabled={isConfirmDisabled}
                    >
                        {confirmLabel}
                    </DialogButton>                </>
            }
        >
            {title && <h3 className="app-dialog-alert-title">{title}</h3>}
            {message && <p className="app-dialog-alert-message">{message}</p>}
        </AppDialog>
    );
}
